import { useEffect, useRef } from 'react';

/*
 * Cursor-reactive borders, driven from one document-level listener.
 *
 * Each registered element gets three custom properties on every frame the
 * pointer moves: --gx / --gy for the cursor's position relative to the element,
 * and --gi for proximity (1 over it, fading to 0 at FALLOFF_PX). The ring is
 * drawn in CSS — see .dd-field in index.css — so this only writes numbers and
 * never touches layout.
 *
 * One listener and one rAF loop serve every card. Cards outside the radius
 * skip their position writes entirely.
 */

const REGISTRY = new Set();
const FALLOFF_PX = 300;

let pointerX = -9999;
let pointerY = -9999;
let frame = null;
let attached = false;

function paint() {
  frame = null;
  REGISTRY.forEach((el) => {
    if (!el.isConnected) {
      REGISTRY.delete(el);
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // Distance to the element's box — zero while the cursor is inside it.
    const outX = Math.max(rect.left - pointerX, 0, pointerX - rect.right);
    const outY = Math.max(rect.top - pointerY, 0, pointerY - rect.bottom);
    const distance = Math.hypot(outX, outY);

    if (distance >= FALLOFF_PX) {
      el.style.setProperty('--gi', '0');
      return;
    }

    // Ease the falloff so the light gathers as you approach rather than
    // ramping linearly across the whole radius.
    const linear = 1 - distance / FALLOFF_PX;
    const intensity = linear * linear;

    el.style.setProperty('--gx', `${(pointerX - rect.left).toFixed(1)}px`);
    el.style.setProperty('--gy', `${(pointerY - rect.top).toFixed(1)}px`);
    el.style.setProperty('--gi', intensity.toFixed(3));
  });
}

function onPointerMove(event) {
  pointerX = event.clientX;
  pointerY = event.clientY;
  if (frame === null) frame = requestAnimationFrame(paint);
}

function attach() {
  if (attached) return;
  attached = true;
  window.addEventListener('pointermove', onPointerMove, { passive: true });
}

function detach() {
  if (!attached || REGISTRY.size > 0) return;
  attached = false;
  window.removeEventListener('pointermove', onPointerMove);
  if (frame !== null) {
    cancelAnimationFrame(frame);
    frame = null;
  }
}

/** Returns a ref to place on the element whose border should react. */
export default function useGlowField(enabled = true) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;

    // No hover on touch, and no ambient movement under reduced motion.
    const fine = window.matchMedia?.('(pointer: fine)').matches;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduced) return undefined;

    REGISTRY.add(el);
    attach();

    return () => {
      REGISTRY.delete(el);
      el.style.removeProperty('--gi');
      detach();
    };
  }, [enabled]);

  return ref;
}
